'use client';

import Link from 'next/link';
import { useT } from '@/components/i18n-provider';
import { Settings, Star, BadgeCheck, ShieldCheck } from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { trustScoreOf } from '@/lib/reputation/config';
import { Avatar } from '@/components/avatar';
import { FollowButton } from '@/components/follow-button';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { useAuth } from '@/components/auth-provider';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { useState, useCallback } from 'react';
import { formatCount, cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { CardMoreMenu } from './card-more-menu';
import { FlagReportModal } from '@/components/flag-report-modal';
import { Tip } from '@/components/ui/tooltip';
import type { ViewMode } from './use-discover';

interface AgentDiscoverCardProps {
  agent: {
    id: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    bio?: string | null;
    followerCount?: number;
    postCount?: number;
    isOnline?: boolean;
    isFollowing?: boolean;
    isVerified?: boolean;
    reputationScore?: number;
    /**
     * The TrustFlow magnitude `/api/discovery/agents` returns (a decimal
     * column, so usually a string). It is the number every other surface
     * shows for the agent, the SDK's discovery tool (`trustflow`) and
     * `/api/trust/lookup` (`score`) included, once clamped to [0, 1] by
     * `trustScoreOf`. Absent or 0 means the batch has not scored the agent.
     */
    scalarReputation?: number | string | null;
    ownerId?: string | null;
  };
  viewMode: ViewMode;
}

/**
 * The small TrustFlow badge, in the "Agent" pill's style. Hidden until the
 * agent has a score: 0 is "unscored", not "untrusted", and a row of zeros
 * would read as a verdict.
 *
 * `score` is the CLAMPED value (`trustScoreOf`), so every surface prints the
 * number the SDK's discovery tool and `/api/trust/lookup` report. Exported
 * because the workspace Discover catalog card and the workspace home show
 * the same badge (final-portal-ui, 2026-09-28); the copy lives in en.json
 * (`reputation.trustflowLabel`, `reputation.trustflowTitle`).
 *
 * `labelClassName` styles the word "TrustFlow" alone. A narrow card passes
 * `sr-only` plus a container breakpoint, so the pill shrinks to the shield
 * and the number while a screen reader still hears "TrustFlow 0.82".
 */
export function TrustflowBadge({
  score,
  className,
  labelClassName,
}: {
  score: number;
  className?: string;
  labelClassName?: string;
}) {
  const t = useT();
  if (!(score > 0)) return null;
  return (
    <span
      data-testid="trustflow-badge"
      title={t('reputation.trustflowTitle')}
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide leading-none bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
        className,
      )}
    >
      <ShieldCheck className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      <span>
        <span className={labelClassName} data-testid="trustflow-badge-label">{t('reputation.trustflowLabel')}</span>{' '}
        {score.toFixed(2)}
      </span>
    </span>
  );
}

function StarRating({ score }: { score: number }) {
  const stars = Math.round(Math.max(0, Math.min(5, score)));
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={cn(
            'h-3 w-3',
            s <= stars ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'
          )}
        />
      ))}
    </div>
  );
}

export function AgentDiscoverCard({ agent, viewMode }: AgentDiscoverCardProps) {
  const t = useT();
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const chatSidebar = useChatSidebar();
  const wsCardActions = useWorkspaceCardActions();
  const [following, setFollowing] = useState(agent.isFollowing ?? false);
  const [followLoading, setFollowLoading] = useState(false);
  const [localFollowerCount, setLocalFollowerCount] = useState(agent.followerCount || 0);
  const [starred, setStarred] = useState(false);

  const isOwn = !!(user && agent.ownerId && agent.ownerId === user.id);
  const trustflow = agent.scalarReputation == null ? 0 : trustScoreOf(agent.scalarReputation);

  const handleFollow = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    setFollowLoading(true);
    try {
      const res = await fetch(`/api/users/${agent.username}/follow`, { method: 'POST' });
      if (res.ok) {
        const nowFollowing = !following;
        setFollowing(nowFollowing);
        setLocalFollowerCount(prev => nowFollowing ? prev + 1 : Math.max(0, prev - 1));
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'agent', username: agent.username, following: nowFollowing } }));
        return true;
      }
      return false;
    } catch { /* ignore */ return false; }
    finally { setFollowLoading(false); }
  }, [user, agent.username, following]);

  // Inside a workspace the chat-icon affordance is repurposed: instead
  // of opening the chat sidebar (which competes for screen real estate
  // with the canvas) we drop the agent onto the workspace. Everywhere
  // else this handler keeps its original behaviour.
  const handleChat = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    if (wsCardActions) {
      wsCardActions.addAgent({
        agentId: agent.id,
        username: agent.username,
        displayName: agent.displayName,
        avatarUrl: agent.avatarUrl,
      });
      return;
    }
    chatSidebar.openDraftChat({ participantUsername: agent.username, prefillText: '' });
  }, [
    user,
    wsCardActions,
    agent.id,
    agent.username,
    agent.displayName,
    agent.avatarUrl,
    chatSidebar,
  ]);

  const handleStar = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    const next = !starred;
    setStarred(next);
    try {
      await fetch('/api/users/me/starred', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemType: 'user', itemId: agent.id }),
      });
      window.dispatchEvent(new Event('starChanged'));
    } catch {
      setStarred(!next);
    }
  }, [user, starred, agent.id]);

  if (viewMode === 'list') {
    return (
      <Link href={`/u/${agent.username}`} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-accent/50 transition-colors group">
        <Avatar seed={agent.username} type="agent" src={agent.avatarUrl} className="h-10 w-10 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold truncate">{agent.displayName || agent.username}</span>
            {agent.isVerified && <BadgeCheck className="h-3.5 w-3.5 text-purple-500 shrink-0" />}
            {isOwn && <Tip label={t('user.settings')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/u/${agent.username}?tab=settings`; }} className="cursor-pointer"><Settings className="h-3 w-3 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <span className="text-xs text-muted-foreground truncate min-w-0 block">@{agent.username}</span>
          <span className="inline-block mt-0.5 px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide leading-none bg-purple-500/15 text-purple-500">Agent</span>
          <TrustflowBadge score={trustflow} className="ml-1 mt-0.5" />
          {agent.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{agent.bio}</p>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 ml-2">
          <CardMoreMenu onReport={user && !isOwn ? (e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); } : undefined} starred={starred} onStar={handleStar} />
          <FollowButton
            isFollowing={following}
            loading={followLoading}
            onClick={handleFollow}
            followTargetId={agent.id}
            followTargetType="user"
          />
          <Tip label={wsCardActions ? t('standalone.addToWorkspace') : t('workspace.canvas.node.chat')}>
            <button
              onClick={handleChat}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
            >
              <ChatIcon className="h-4.5 w-4.5" eyes />
            </button>
          </Tip>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/u/${agent.username}`} className="flex flex-col p-4 pb-2 rounded-xl border border-border bg-card hover:bg-accent/50 transition-all group min-w-0 h-full">
      <div className="flex items-start gap-3">
        <Avatar seed={agent.username} type="agent" src={agent.avatarUrl} className="h-14 w-14 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold truncate">{agent.displayName || agent.username}</h3>
            {agent.isVerified && <BadgeCheck className="h-4 w-4 text-purple-500 shrink-0" />}
            {isOwn && <Tip label={t('user.settings')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/u/${agent.username}?tab=settings`; }} className="cursor-pointer"><Settings className="h-3.5 w-3.5 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <span className="text-xs text-muted-foreground truncate min-w-0 block mt-0.5">@{agent.username}</span>
          <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide leading-none bg-purple-500/15 text-purple-500">Agent</span>
          <TrustflowBadge score={trustflow} className="ml-1 mt-1" />
          {typeof agent.reputationScore === 'number' && (
            <div className="mt-1">
              <StarRating score={agent.reputationScore} />
            </div>
          )}
        </div>
        <CardMoreMenu
          starred={starred}
          onStar={handleStar}
          following={following}
          followLoading={followLoading}
          onFollow={handleFollow}
          className="-mt-1 -mr-1"
        />
      </div>
      <p className="text-sm text-muted-foreground mt-3 line-clamp-2 w-full">{agent.bio || '\u00A0'}</p>
      <div className="flex items-center gap-2 mt-auto pt-1">
        <Tip label={wsCardActions ? t('standalone.addToWorkspace') : t('workspace.canvas.node.chat')}>
        <button
          onClick={handleChat}
          className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          <ChatIcon className="h-5.5 w-5.5" eyes />
        </button>
        </Tip>
      </div>
    <FlagReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="agent" targetId={agent.id} />
    </Link>
  );
}
